import { useMemo, useState, useEffect } from "react";

import { getSelectorsByUserAgent } from "react-device-detect";

import { ProductKey } from "@/components/signup/utils";
import { GrowthUnifiedProfile } from "@/libraries/growth-unified-profile-client";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";
import { Cookie } from "@/utils/cookie";
import { useLocation } from "@/utils/location-provider";
import { useEnglishLocale } from "@/utils/sentry-intl";
import { useSSRLoginState } from "@/utils/ssr-login-state-provider";

import { useUserContext } from "../user-context-provider";

import { daysAgo } from "./days-ago";

export const COHORTS = Object.freeze({
    NEW_USER: "new-user",
    EXISTING_USER: "existing-user",
    MANAGED_USER: "managed-user",
    WEEK_ONE_USER: "week-one-user",
    UPGRADE_ELIGIBLE_USER: "upgrade-eligible-user",
    UPLIFT_ELIGIBLE_USER: "uplift-eligible-user",
    CURRENT_PRODUCT_USER: "current-product-user",
    CROSSFLOW_ELIGIBLE_USER: "crossflow-eligible-user"
});

export type Cohort = keyof typeof COHORTS;

/**
 * Get user's primary cohort
 *
 * @returns Cohort | undefined
 */
export const useCohort = () => {
    const { profile, product } = useUserContext();
    const isManagedUser = useUserIsManagedByGuardOrgAndHasSites();
    const isWeekOneUser = useProductD0to6Site({
        productKey: product?.key
    });
    const isNewUser = useNewUser();
    const isUpgradeEligible = useIsUpgradeEligible();
    const isUpliftEligible = useIsUpliftEligible();
    const isCurrentProductUser = useIsCurrentProductUser();
    const isCrossflowEligible = useIsCrossflowEligible();
    if (profile.requestInProgress) {
        return undefined;
    }
    if (isManagedUser) {
        return COHORTS.MANAGED_USER;
    }
    if (isUpgradeEligible) {
        return COHORTS.UPGRADE_ELIGIBLE_USER;
    }
    if (isWeekOneUser) {
        return COHORTS.WEEK_ONE_USER;
    }
    if (isCurrentProductUser) {
        return COHORTS.CURRENT_PRODUCT_USER;
    }
    if (isUpliftEligible) {
        return COHORTS.UPLIFT_ELIGIBLE_USER;
    }
    if (isNewUser) {
        return COHORTS.NEW_USER;
    }
    if (isCrossflowEligible) {
        return COHORTS.CROSSFLOW_ELIGIBLE_USER;
    }
    return COHORTS.EXISTING_USER;
};

/**
 * User is logged in and has only one site which was created in the last week and is Confluence-only
 * @returns boolean | undefined
 */
export const useConfluenceD0to6 = () => {
    const d1to6ConfluenceSite = useProductD0to6Site({
        productKey: ProductKey.CONFLUENCE
    });
    return !d1to6ConfluenceSite ? d1to6ConfluenceSite : true;
};

/**
 * User's first name
 * @returns string | undefined
 */
export const useFirstName = () => {
    const { user } = useUserContext();
    if (user.requestInProgress) {
        return undefined;
    }
    return (user.name ?? "").split(" ")[0];
};

/**
 * Returns true if the user has only one site which was created in the two weeks and is Jira-only
 * @returns boolean | undefined
 */
export const useJiraD0toW2 = () => {
    const d1toW2JiraSite = useJiraD0toW2Site();
    return !d1toW2JiraSite ? d1toW2JiraSite : true;
};

/**
 * Returns true if the user has only one site which was created within 6 days and is Jira-only
 * @returns boolean | undefined
 */
export const useJiraD0to6 = () => {
    const jiraD0to6Site = useProductD0to6Site({
        productKey: ProductKey.JIRA_SOFTWARE
    });
    return !jiraD0to6Site ? jiraD0to6Site : true;
};

/**
 * User is logged in and has a single Jira site created in the last 2 weeks and is coming from Jira.com
 * @returns boolean | undefined
 */
export const useJiraD0toW2Site = () => {
    const {
        profile: { attributes, requestInProgress },
        referrer
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) return undefined;
        if (attributes?.existingSites.length !== 1 || referrer !== "jira.com") {
            return false;
        }

        const twoWeeksAgo = daysAgo(14);

        return (
            attributes?.existingSites.find(
                ({ siteCreatedAt, products }) =>
                    siteCreatedAt > twoWeeksAgo &&
                    products.some(
                        ({ productKey }) =>
                            productKey === ProductKey.JIRA_SOFTWARE
                    ) &&
                    products.every(
                        (product) =>
                            product.productKey === ProductKey.JIRA_SOFTWARE ||
                            product.productKey === ProductKey.ROVO
                    )
            ) ?? false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress, referrer]);
};

/**
 * Returns true if the user has multiple Jira sites or one Jira site created more than 7 days ago
 * @returns boolean | undefined
 */
export const useJiraW2AndAfterOrMultipleJiras = () => {
    const oneW2OrOlderJiraSite = useOneW2OrOlderJiraSite();
    const multipleJiraSites = useMultipleJiraSites();

    if (oneW2OrOlderJiraSite === undefined || multipleJiraSites === undefined) {
        return undefined;
    }

    return oneW2OrOlderJiraSite || multipleJiraSites;
};

/**
 * Cohort for JFind1504. If the paid anchor is in the d0AndAfterAnchors array, returns true if user is logged in and has Jira
 * If paid anchor is not in the d0AndAfterAnchors array, returns true if user is W2 and after or has multiple Jiras
 * @returns boolean
 */
export const useGetUsersBackToProductAfterD7Cohort = (paidAnchor: string) => {
    const loggedInHasJira = useLoggedInHasJira();
    const isJiraW2AndAfterOrHasMultipleJiras =
        useJiraW2AndAfterOrMultipleJiras();

    if (paidAnchor === undefined) {
        return false;
    }

    const d0AndAfterAnchors = [
        "anchor-lp-jsw-team-engineering",
        "anchor-lp-jsw-team-marketing",
        "anchor-lp-jsw-team-hr",
        "anchor-lp-jsw-team-legal",
        "anchor-lp-jsw-team-operations",
        "anchor-lp-jsw-team-design",
        "anchor-lp-jsw-team-programmgmt",
        "anchor-lp-jsw-team-sales"
    ];

    if (d0AndAfterAnchors.includes(paidAnchor)) {
        return loggedInHasJira;
    } else {
        return isJiraW2AndAfterOrHasMultipleJiras;
    }
};

/**
 * User is logged in and has a single Jira site created more than 7 days ago
 * @returns boolean | undefined
 */
export const useOneW2OrOlderJiraSite = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) return undefined;

        const oneWeekAgo = daysAgo(7);

        const matchingSitesCount =
            attributes?.existingSites.filter(
                ({ siteCreatedAt, products }) =>
                    siteCreatedAt < oneWeekAgo &&
                    products.some(
                        ({ productKey }) =>
                            productKey === ProductKey.JIRA_SOFTWARE
                    )
            ).length ?? 0;

        return matchingSitesCount === 1;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress]);
};

/**
 * User is logged in and has a multiple Jira sites
 * @returns boolean | undefined
 */
export const useMultipleJiraSites = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) return undefined;

        const matchingSitesCount =
            attributes?.existingSites.filter(({ products }) =>
                products.some(
                    ({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE
                )
            ).length ?? 0;

        return matchingSitesCount > 1;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress]);
};

/**
 * User is logged out
 * @returns boolean | undefined
 */
export const useLoggedOut = () => {
    const {
        user: { requestInProgress, isUserLoggedIn }
    } = useUserContext();
    const { hasCloudSessionTokenCookie } = useSSRLoginState();

    if (typeof window === "undefined") {
        // If SSR return if cloud session token cookie is present
        return hasCloudSessionTokenCookie === false;
    }

    if (requestInProgress) {
        // Cloud session token cookie is created as http only cookie, so can't reference from browser's JS.
        // Instead, using login_user_detected cookie.
        const loginUserDetectedCookie = Cookie.readCookie(
            "login_user_detected"
        );
        return (
            loginUserDetectedCookie === undefined ||
            loginUserDetectedCookie !== "true"
        );
    }
    return isUserLoggedIn === false;
};

/**
 * User is logged in
 * @returns boolean | undefined
 */
export const useLoggedIn = () => {
    const {
        user: { requestInProgress, isUserLoggedIn }
    } = useUserContext();

    if (requestInProgress) {
        return undefined;
    }

    return isUserLoggedIn;
};

/**
 * User is logged out and has Jira
 * @returns boolean | undefined
 */
export const useLoggedOutHasJira = () => {
    const loggedOut = useLoggedOut();
    const { loggedOutProfile } = useUserContext();

    if (loggedOut === undefined) {
        return undefined;
    }
    if (!loggedOut) {
        return false;
    }
    return loggedOutProfile?.sites?.some(({ products }) =>
        products?.some(
            ({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE
        )
    );
};

/**
 * User is logged out and is in the Confluence flow (any edition)
 * @returns boolean | undefined
 */
export const useLoggedOutConfluence = () => {
    const confluence = useConfluenceIntent();
    const loggedOut = useLoggedOut();
    return confluence && loggedOut;
};

/**
 * User is logged out and is in the Jira flow (any edition)
 * @returns boolean | undefined
 */
export const useLoggedOutJira = () => {
    const jira = useJiraIntent();
    const loggedOut = useLoggedOut();
    return jira && loggedOut;
};

/**
 * User is logged out and does not have Jira
 * @returns boolean | undefined
 */
export const useLoggedOutNoJira = () => {
    const loggedOut = useLoggedOut();
    const { loggedOutProfile } = useUserContext();

    if (loggedOut === undefined) {
        return undefined;
    }
    if (!loggedOut) {
        return false;
    }

    return (
        !loggedOutProfile ||
        loggedOutProfile.sites?.every(
            ({ products }) =>
                !products?.some(
                    ({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE
                )
        )
    );
};

/**
 * User is logged out and is in the Jira free flow
 * @returns boolean | undefined
 */
export const useLoggedOutJiraFree = () => {
    const jira = useJiraIntent();
    const free = useFreeIntent();
    const loggedOut = useLoggedOut();
    return jira && free && loggedOut;
};

/**
 * User is logged in and has no sites
 * @returns boolean | undefined
 */
export const useNewUser = () => {
    const {
        profile: { requestInProgress, cohorts }
    } = useUserContext();
    if (requestInProgress) return undefined;
    return cohorts?.userType === "new-user";
};

/**
 * Get first site with given productKey created in the last 7 days
 * @param productKey string
 * @returns object | undefined
 */
export const useProductD0to6Site = ({
    productKey
}: {
    productKey?: ProductKey;
}) => {
    const {
        profile: { requestInProgress, attributes }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) return undefined;
        if (attributes?.existingSites.length !== 1) {
            return false;
        }

        const oneWeekAgo = daysAgo(7);

        // sites may only have the current productKey, rovo, project, or goal
        const weekOneProductKeys = [
            productKey,
            ProductKey.ROVO,
            "project",
            "goal"
        ];

        return (
            attributes?.existingSites.find(({ siteCreatedAt, products }) => {
                const isRecent = siteCreatedAt > oneWeekAgo;
                const hasTargetProduct = products.some(
                    (product) => product.productKey === productKey
                );
                const allProductsAllowed = products.every(({ productKey }) =>
                    weekOneProductKeys.includes(productKey)
                );
                return isRecent && hasTargetProduct && allProductsAllowed;
            }) ?? false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress, productKey]);
};

/**
 * Get all sites with the current product
 *
 * @returns array | undefined
 */
export const useSitesWithCurrentProduct = () => {
    const {
        product,
        profile: { attributes, requestInProgress }
    } = useUserContext();

    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        if (!product?.key) {
            return [];
        }

        return (
            attributes?.existingSites.filter((site) =>
                site.products.some(
                    ({ productKey, partOfCollection }) =>
                        productKey === product?.key ||
                        partOfCollection === product?.key
                )
            ) ?? []
        );
    }, [requestInProgress, attributes?.existingSites, product?.key]);
};

/**
 * User is logged in and has one or more Jira sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJira = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.some((sites) =>
                sites.products.some(
                    (product) => product.productKey === ProductKey.JIRA_SOFTWARE
                )
            ) ?? false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress]);
};

/**
 * User is logged in and has one or more Confluence sites
 * @returns boolean | undefined
 */
export const useLoggedInHasConfluence = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.some((sites) =>
                sites.products.find(
                    (product) => product.productKey === ProductKey.CONFLUENCE
                )
            ) ?? false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestInProgress]);
};

/**
 * User is logged in and has one or more JSM sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJSM = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.some((sites) =>
                sites.products.find(
                    (product) =>
                        product.productKey ===
                        ProductKey.JIRA_SERVICE_MANAGEMENT
                )
            ) ?? false
        );
    }, [requestInProgress]);
};

/**
 * User is logged in and has one or more Loom sites
 * @returns boolean | undefined
 */
export const useLoggedInHasLoom = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.some((sites) =>
                sites.products.find(
                    (product) => product.productKey === ProductKey.LOOM
                )
            ) ?? false
        );
    }, [requestInProgress]);
};

/**
 * User is logged in and has one or more JPD sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJPD = () => {
    const {
        profile: { attributes, requestInProgress }
    } = useUserContext();
    return useMemo(() => {
        if (requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.some((sites) =>
                sites.products.find(
                    (product) =>
                        product.productKey === ProductKey.JIRA_PRODUCT_DISCOVERY
                )
            ) ?? false
        );
    }, [requestInProgress]);
};

/**
 * User is logged in AND has one or more Jira sites AND has one or more Confluence sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJiraAndConfluence = () => {
    const hasJiraSites = useLoggedInHasJira();
    const hasConfluenceSites = useLoggedInHasConfluence();

    return hasJiraSites && hasConfluenceSites;
};

const JSM_ELIGIBLE_JOB_FUNCTIONS = new Set([
    "engineering",
    "information technology",
    "customer service",
    "operations",
    "human resources",
    "data science"
]);

const LOOM_ELIGIBLE_JOB_FUNCTIONS = new Set([
    "program management",
    "finance",
    "legal",
    "c-suite",
    "other",
    "undefined",
    null,
    "sales",
    "marketing",
    "design"
]);

const JPD_ELIGIBLE_JOB_FUNCTIONS = new Set(["product management"]);

/**
 * User has a job function that pairs with JSM
 * @returns boolean | undefined
 */
export const useJSMJobFunction = () => {
    const { getUserProfileJobFunctionCallback } = useGupsStatus();
    const [hasJSMJobFunction, setHasJSMJobFunction] = useState<
        boolean | undefined
    >(undefined);

    useEffect(() => {
        const checkTeamType = async () => {
            const result = await getUserProfileJobFunctionCallback(
                "homepage-xflow"
            );
            const jobFunction = (
                result as { data?: GrowthUnifiedProfile["userProfile"] }
            )?.data?.jobFunction;
            const matches =
                jobFunction && JSM_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
            setHasJSMJobFunction(matches || false);
        };

        checkTeamType();
    }, [getUserProfileJobFunctionCallback]);

    return hasJSMJobFunction;
};

/**
 * User is in the JSM experiment cohort
 * @returns boolean
 */
export const useLoggedInHasJSMCohortQualification = () => {
    const hasJSMJobFunction = useJSMJobFunction();
    const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
    const isEnglishLocale = useEnglishLocale();

    return hasJSMJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User has a job function that pairs with JPD
 * @returns boolean | undefined
 */
export const useJPDJobFunction = () => {
    const { getUserProfileJobFunctionCallback } = useGupsStatus();
    const [hasJPDJobFunction, setHasJPDJobFunction] = useState<
        boolean | undefined
    >(undefined);

    useEffect(() => {
        const checkTeamType = async () => {
            const result = await getUserProfileJobFunctionCallback(
                "homepage-xflow"
            );
            const jobFunction = (
                result as { data?: GrowthUnifiedProfile["userProfile"] }
            )?.data?.jobFunction;
            const matches =
                jobFunction && JPD_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
            setHasJPDJobFunction(matches || false);
        };

        checkTeamType();
    }, [getUserProfileJobFunctionCallback]);

    return hasJPDJobFunction;
};

/**
 * User is in the JDP experiment cohort
 * @returns boolean
 */
export const useLoggedInHasJPDCohortQualification = () => {
    const hasJPDJobFunction = useJPDJobFunction();
    const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
    const isEnglishLocale = useEnglishLocale();

    return hasJPDJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User has a team type that pairs with Loom
 * @returns boolean | undefined
 */
export const useLoomJobFunction = () => {
    const { getUserProfileJobFunctionCallback } = useGupsStatus();
    const [hasLoomJobFunction, setHasLoomJobFunction] = useState<
        boolean | undefined
    >(undefined);

    useEffect(() => {
        const checkTeamType = async () => {
            const result = await getUserProfileJobFunctionCallback(
                "homepage-xflow"
            );
            const jobFunction = (
                result as { data?: GrowthUnifiedProfile["userProfile"] }
            )?.data?.jobFunction;
            const matches =
                jobFunction == null ||
                LOOM_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
            setHasLoomJobFunction(matches || false);
        };

        checkTeamType();
    }, [getUserProfileJobFunctionCallback]);

    return hasLoomJobFunction;
};

/**
 * User is in the Loom experiment cohort
 * @returns boolean
 */
export const useLoggedInHasLoomCohortQualification = () => {
    const hasLoomJobFunction = useLoomJobFunction();
    const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
    const isEnglishLocale = useEnglishLocale();

    return hasLoomJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User is in the JSM Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageJSMXflowTargetingCohort = () => {
    const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
    const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
    const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
    const hasJSM = useLoggedInHasJSM();
    const hasLoom = useLoggedInHasLoom();
    const hasJPD = useLoggedInHasJPD();

    if (
        // “JSM team types | job function” and no JSM entitlement
        hasJSMCohortQualification &&
        !hasJSM
    ) {
        return true;
    } else if (
        // “Loom team types | job function” and has loom and no JSM entitlement
        hasLoomCohortQualification &&
        hasLoom &&
        !hasJSM
    ) {
        return true;
    } else if (
        // “JPD team types | job function” and has loom and JPD entitlement and no JSM entitlement
        hasJPDCohortQualification &&
        hasLoom &&
        hasJPD &&
        !hasJSM
    ) {
        return true;
    }
    return false;
};

/**
 * User is in the JPD Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageJPDXflowTargetingCohort = () => {
    const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
    const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
    const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
    const hasJSM = useLoggedInHasJSM();
    const hasLoom = useLoggedInHasLoom();
    const hasJPD = useLoggedInHasJPD();

    if (
        // “JPD team types | job function” and no JPD entitlement
        hasJPDCohortQualification &&
        !hasJPD
    ) {
        return true;
    } else if (
        // “Loom team types | job function” and has loom and has JSM and no JPD entitlement
        hasLoomCohortQualification &&
        hasLoom &&
        hasJSM &&
        !hasJPD
    ) {
        return true;
    } else if (
        // “JSM team types | job function” and has JSM and has loom and no JPD entitlement
        hasJSMCohortQualification &&
        hasJSM &&
        hasLoom &&
        !hasJPD
    ) {
        return true;
    }
    return false;
};

/**
 * User is in the Loom Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageLoomXflowTargetingCohort = () => {
    const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
    const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
    const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
    const hasJSM = useLoggedInHasJSM();
    const hasLoom = useLoggedInHasLoom();
    const hasJPD = useLoggedInHasJPD();

    if (
        // "Loom team types | job function” and no Loom entitlement
        hasLoomCohortQualification &&
        !hasLoom
    ) {
        return true;
    } else if (
        // “JSM team types | job function” and has JSM and no Loom entitlement
        hasJSMCohortQualification &&
        hasJSM &&
        !hasLoom
    ) {
        return true;
    } else if (
        // “JPD team types | job function” and has JPD and no Loom entitlement
        hasJPDCohortQualification &&
        hasJPD &&
        !hasLoom
    ) {
        return true;
    }
    return false;
};

/**
 * Returns true if the user prefers dark color scheme
 * @returns boolean
 */
export const usePrefersDarkColorScheme = () => {
    return usePrefersColorScheme("dark");
};

/**
 * Returns true if the user prefers the specified color scheme and is on a specific path
 * @param color "dark" | "light"
 * Returns true if the user prefers the specified color scheme and is on a specific path
 * This is used to determine if dark mode should be applied based on user preference
 * and the current path.
 * @returns boolean
 */
export const usePrefersColorScheme = (color: "dark" | "light") => {
    return window?.matchMedia &&
        window?.matchMedia(`(prefers-color-scheme: ${color})`).matches
        ? true
        : false;
};

/**
 * User is in the Confluence free flow
 * @returns boolean
 */
export const useConfluenceFreeIntent = () => {
    const isConfluence = useConfluenceIntent();
    const isFree = useFreeIntent();
    return isConfluence && isFree;
};

/**
 * User is in the Confluence flow (any edition)
 * @returns boolean
 */
export const useConfluenceIntent = () => {
    const { product } = useUserContext();
    return product?.name === "confluence";
};

/**
 * User is in a free flow (any product)
 * @returns boolean
 */
export const useFreeIntent = () => {
    const { edition } = useUserContext();
    return edition === "free";
};

/**
 * User is in a standard flow (any product)
 * @returns boolean
 */
export const useStandardIntent = () => {
    const { edition } = useUserContext();
    return edition === "standard";
};

/**
 * User is in a premium flow (any product)
 * @returns boolean
 */
export const usePremiumIntent = () => {
    const { edition } = useUserContext();
    return edition === "premium";
};

/**
 * User is in the Jira flow (any edition)
 * @returns boolean
 */
export const useJiraIntent = () => {
    const { product } = useUserContext();
    return product?.name === "jira";
};

/**
 * User is in the Jira flow (free edition)
 * @returns boolean
 */
export const useJiraFreeIntent = () => {
    const { edition, product } = useUserContext();
    return product?.name === "jira" && edition === "free";
};

/**
 * User is in the Jira Service Management free flow
 * @returns boolean
 */
export const useJiraServiceManagementFreeIntent = () => {
    const isJiraServiceManagement = useJiraServiceManagementIntent();
    const isFree = useFreeIntent();
    return isJiraServiceManagement && isFree;
};

/**
 * User is in the Jira Service Management flow (any edition)
 * @returns boolean
 */
export const useJiraServiceManagementIntent = () => {
    const { product } = useUserContext();
    return product?.name === "jira-servicedesk";
};

/**
 * User is in the Jira Product Discovery free flow
 * @returns boolean
 */
export const useJiraProductDiscoveryFreeIntent = () => {
    const isJiraProductDiscovery = useJiraProductDiscoveryIntent();
    const isFree = useFreeIntent();
    return isJiraProductDiscovery && isFree;
};

/**
 * User is in the Jira Product Discovery flow (any edition)
 * @returns boolean
 */
export const useJiraProductDiscoveryIntent = () => {
    const { product } = useUserContext();
    return product?.name === "jira-product-discovery";
};

/**
 * User is in the Dev AI flow
 * @returns boolean
 */
export const useDevAiIntent = () => {
    const { product } = useUserContext();
    return product?.name === "devai";
};

/**
 * User is in the Loom flow (any edition)
 * @returns boolean
 */
export const useLoomIntent = () => {
    const { product } = useUserContext();
    return product?.name === "loom";
};

export const useManagingOrgHasGuard = () => {
    const { profile } = useUserContext();

    // todo share with bxp-ui-signup's orgBasedOfferingIdMap
    const GUARD_PREMIUM = "2f5e5808-d501-450a-b246-b0bd50a0b72a";
    const GUARD_STANDARD = "1ac3b7dd-7141-414e-bc00-c890acc458b0";
    const GUARD_HAMS = "GUARD_HAMS";
    const GUARD_OFFERING_IDS = [GUARD_PREMIUM, GUARD_STANDARD, GUARD_HAMS];

    if (profile.requestInProgress) {
        return undefined;
    }

    return profile.attributes?.managingOrg?.offeringIds?.some((offeringId) =>
        GUARD_OFFERING_IDS.includes(offeringId)
    );
};

export const useManagingOrgUserSites = () => {
    const { product, profile } = useUserContext();

    if (profile.requestInProgress) {
        return undefined;
    }

    return profile.attributes?.existingSites.filter(
        ({ cloudId, products }) =>
            profile.attributes?.managingOrg?.cloudIds?.includes(cloudId) &&
            products.some(({ productKey }) => productKey === product?.key)
    );
};

/**
 * Returns user's existing sites that do not have the current product and the user has admin access to
 *
 * @return {ExistingSite[]} array of existing sites
 */
export const useCrossflowEligibleSites = () => {
    const { product, profile } = useUserContext();
    return useMemo(
        () =>
            profile.attributes?.existingSites.filter(
                ({ hasAdminAccess, products }) =>
                    product &&
                    hasAdminAccess &&
                    !products.some(({ productKey }) =>
                        product.key === ProductKey.SERVICE_COLLECTION
                            ? // exclude sites with servco constituent products from site activation crossflow
                              [
                                  ProductKey.JIRA_SERVICE_MANAGEMENT,
                                  ProductKey.JIRA_CUSTOMER_SERVICE
                              ].includes(productKey as ProductKey)
                            : productKey === product.key
                    )
            ) ?? [],
        [profile.attributes?.existingSites, product]
    );
};

export const useIsCrossflowEligible = () => {
    const crossflowEligibleSites = useCrossflowEligibleSites();
    return crossflowEligibleSites?.length > 0;
};

export const useUserIsManagedByGuardOrgAndHasSites = () => {
    const { profile } = useUserContext();
    const managingOrgHasGuard = useManagingOrgHasGuard();
    const managingOrgUserSites = useManagingOrgUserSites();

    if (profile.requestInProgress) {
        return undefined;
    }

    return managingOrgHasGuard && managingOrgUserSites?.length;
};

export const useLoggedInHasJiraAndNotConfluence = () => {
    const {
        profile: { requestInProgress: profileRequestInProgress, attributes },
        user: { requestInProgress: userRequestInProgress }
    } = useUserContext();
    return useMemo(() => {
        // Checking for both user and profile request in progress,
        // since profileRequestInProgress is initially defined as 'false' and then gets updated to true when the request is made.
        // This is probably not needed since this experiment is wrapped within <LoggedInHeroContainer /> and has the same logic.
        // But adding it here incase this experiment is used outside of <LoggedInHeroContainer />
        if (userRequestInProgress || profileRequestInProgress) {
            return undefined;
        }
        return (
            (attributes?.existingSites.some((sites) =>
                sites.products.find(
                    (product) => product.productKey === ProductKey.JIRA_SOFTWARE
                )
            ) &&
                !attributes?.existingSites.some((sites) =>
                    sites.products.find(
                        (product) =>
                            product.productKey === ProductKey.CONFLUENCE
                    )
                )) ??
            false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRequestInProgress, profileRequestInProgress]);
};

/**
 * Return true if given pathname matches current pathname
 * @param pathnames string[]
 * @returns boolean
 */
export const useSpecifiedPathnames = (pathnames: string[]) => {
    const { pathname: currentPathname } = useLocation();

    if (pathnames.includes(currentPathname)) {
        return true;
    }
    return false;
};

/**
 * User is in a paid edition flow and has the free version of current product
 * @returns boolean
 */
export const useIsUpgradeEligible = () => {
    const { product, edition } = useUserContext();
    const upgradableSites = useUpgradableSites();

    // you need to be in a paid product edition flow
    if (!product || edition === "free") {
        return undefined;
    }

    return !!upgradableSites?.length;
};

export const useIsUpliftEligible = () => {
    const upliftableSites = useUpliftableSites();
    return !!upliftableSites?.length;
};

/**
 * Get a list of sites that are eligible for upgrade (have free edition of the current product)
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useUpgradableSites = () => {
    const {
        product,
        profile: { attributes, requestInProgress }
    } = useUserContext();

    return useMemo(() => {
        if (!product?.key || requestInProgress) {
            return undefined;
        }

        return (
            attributes?.existingSites.filter(({ products }) =>
                products.some(
                    ({ productKey, productEdition, partOfCollection }) =>
                        (productKey === product.key ||
                            partOfCollection === product.key) &&
                        productEdition === "free"
                )
            ) ?? []
        );
    }, [product?.key, attributes?.existingSites, requestInProgress]);
};

/**
 * Get a list of sites that are not part of a collection and eligible for uplift (have constituent products of current collection)
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useUpliftableSites = () => {
    const {
        product,
        profile: { attributes, requestInProgress }
    } = useUserContext();

    return useMemo(() => {
        if (!product?.key || requestInProgress) {
            return undefined;
        }

        // currently only applies to teamwork collection
        if (product.key !== ProductKey.TEAMWORK_COLLECTION) {
            return [];
        }

        const constituentProducts = [
            ProductKey.JIRA_SOFTWARE,
            ProductKey.CONFLUENCE,
            ProductKey.LOOM
        ] as string[];

        return (
            attributes?.existingSites.filter(({ products }) =>
                products.some(
                    ({ productKey, partOfCollection }) =>
                        constituentProducts.includes(productKey) &&
                        !partOfCollection
                )
            ) ?? []
        );
    }, [product?.key, attributes?.existingSites, requestInProgress]);
};

/**
 * User is in the Service Collection flow (any edition)
 * @returns {boolean|undefined}
 */
export const useServiceCollectionIntent = () => {
    const { product } = useUserContext();
    if (!product) {
        return undefined;
    }
    return product.key === ProductKey.SERVICE_COLLECTION;
};

/**
 * User is in the Teamwork Collection flow (any edition)
 * @returns {boolean|undefined}
 */
export const useTeamworkCollectionIntent = () => {
    const { product } = useUserContext();
    if (!product) {
        return undefined;
    }
    return product.key === ProductKey.TEAMWORK_COLLECTION;
};

/**
 * User has the Service Collection (any edition)
 * @returns {boolean|undefined}
 */
export const useHasServiceCollection = () => {
    const { profile } = useUserContext();
    if (profile.requestInProgress) {
        return undefined;
    }
    return profile.attributes?.existingSites.some(({ products }) =>
        products.some(
            ({ partOfCollection }) =>
                partOfCollection === ProductKey.SERVICE_COLLECTION
        )
    );
};

/**
 * User has the Teamwork Collection (any edition)
 * @returns {boolean|undefined}
 */
export const useHasTeamworkCollection = () => {
    const { profile } = useUserContext();
    if (profile.requestInProgress) {
        return undefined;
    }
    return profile.attributes?.existingSites.some(({ products }) =>
        products.some(
            ({ partOfCollection }) =>
                partOfCollection === ProductKey.TEAMWORK_COLLECTION
        )
    );
};

/**
 * User is in Desktop view
 * Uses request.headers['user-agent'] on server side
 * Uses window.navigator.userAgent on client side
 * @returns {boolean|undefined}
 */
export const useIsDesktop = () => {
    const { userAgent } = useSSRLoginState();
    const { isDesktop } =
        userAgent === undefined ? {} : getSelectorsByUserAgent(userAgent) ?? {};
    return isDesktop;
};

/**
 * User has the current product
 * @returns {boolean|undefined}
 */
export const useIsCurrentProductUser = () => {
    const { product, profile } = useUserContext();
    const hasServiceCollection = useHasServiceCollection();
    const hasTeamworkCollection = useHasTeamworkCollection();
    if (profile.requestInProgress) {
        return undefined;
    }
    if (product?.key === ProductKey.SERVICE_COLLECTION) {
        return hasServiceCollection;
    }
    if (product?.key === ProductKey.TEAMWORK_COLLECTION) {
        return hasTeamworkCollection;
    }
    return profile.attributes?.existingSites.some(({ products }) =>
        products.some(({ productKey }) => productKey === product?.key)
    );
};

/**
 * User is logged out and has existing sites
 * @returns boolean | undefined
 */
export const useLoggedOutHasExistingSites = () => {
    const loggedOut = useLoggedOut();
    const { loggedOutProfile } = useUserContext();

    if (loggedOut === undefined) {
        return undefined;
    }
    if (!loggedOut) {
        return false;
    }

    return (
        loggedOutProfile?.sites &&
        loggedOutProfile.sites.length > 0 &&
        loggedOutProfile.sites.some(
            ({ products }) => products && products.length > 0
        )
    );
};

/**
 * User is logged out and has no existing sites
 * @returns boolean | undefined
 */
export const useLoggedOutHasNoExistingSites = () => {
    const isLoggedOut = useLoggedOut();
    const { loggedOutProfile } = useUserContext();

    if (isLoggedOut === undefined) {
        return undefined;
    }
    if (!isLoggedOut) {
        return false;
    }

    const hasSites =
        loggedOutProfile?.sites &&
        loggedOutProfile.sites.length > 0 &&
        loggedOutProfile.sites.some(
            ({ products }) => products && products.length > 0
        );

    return !hasSites;
};

/**
 * Returns true if the user has multiple transaction accounts
 * This cohort is used for targeting users who should see the transaction account selector
 * @returns boolean
 */
export const useMultipleTransactionAccountsCohort = () => {
    const { transactionAccounts } = useUserContext();
    return transactionAccounts.length > 1;
};

/**
 * Returns true if the user has any transaction account with non-null partner status
 * This cohort checks if any transaction account has partnerStatus with non-null values
 * @returns boolean
 */
export const useHasPartnerStatusCohort = () => {
    const { transactionAccounts } = useUserContext();

    const result = transactionAccounts.some((account) => {
        const { partnerStatus } = account;

        // Check if any field in partnerStatus is not null
        return (
            partnerStatus.discountTier !== null ||
            partnerStatus.programLevelGlobal !== null ||
            partnerStatus.externalSystemIdentifier !== null ||
            partnerStatus.programLevel !== null
        );
    });

    console.log(
        "useHasPartnerStatusCohort result:",
        result,
        "transactionAccounts:",
        transactionAccounts
    );

    return result;
};

/**
 * Returns true if user is logged in AND has multiple transaction accounts AND has partner status
 * This is a composite cohort hook for the signup transaction selector experiment
 * @returns boolean | undefined
 */
export const useSignupTransactionSelectorCohort = () => {
    const isLoggedIn = useLoggedIn();
    const hasPartnerStatus = useHasPartnerStatusCohort();
    const hasMultipleAccounts = useMultipleTransactionAccountsCohort();

    return isLoggedIn && hasPartnerStatus && hasMultipleAccounts;
};
